import { BehaviorSubject } from "rxjs"
import {
    IResult,
    Category,
    CreateCategory,
    CreateCategoryFactory,
    ModifyCategory,
    ModifyCategoryFactory,
    Location,
    CreateLocation,
    CreateLocationFactory,
    ModifyLocation,
    ModifyLocationFactory,
    Application,
    ModifyApplication,
    ModifyApplicationFactory,
    AverageMonthlyIncome,
    CreateAverageMonthlyIncome,
    ModifyAverageMonthlyIncome,
    CreateAverageMonthlyIncomeFactory,
    ModifyAverageMonthlyIncomeFactory,
    PositionScore,
    Position,
} from ".."

export interface ISubjects {
    items: BehaviorSubject<any[]>
    currentAddItem: BehaviorSubject<any>
    currentEditItem: BehaviorSubject<any>
    result: BehaviorSubject<IResult | null>
}

export interface IContextState {
    subjects: ISubjects
    currentEditedMasterId: string | undefined
    resetAddItem: () => void
    resetEditItem: (masterId: string | undefined) => void
    getKey: () => string
    setFieldAdd: (key: string, value: any) => void
    setFieldEdit: (key: string, value: any) => void
    sendResult: (result: IResult) => void
}

export interface IKngSubmittedApplicationsState {
    submittedApplications: BehaviorSubject<Application[]>
    currentApplication: BehaviorSubject<ModifyApplication | null>
    currentSuperior: BehaviorSubject<string>
    currentIndex: BehaviorSubject<number>
    setFieldCurrentApplication: (key: string, value: any) => void
    nextApplication: () => void
    removeApplication: () => void
    setCurrentApplication: (index: number) => void
    getChangedApplications: () => ModifyApplication[]
    sendResult: (result: IResult) => void
    result: BehaviorSubject<IResult | null>
    init: (apps: Application[]) => void
}

export interface IKngPositionScoresState {
    positionScores: BehaviorSubject<PositionScore[]>
    unassignedPositions: BehaviorSubject<Position[]>
    addPositionScore: (positionScore: PositionScore) => void
    updatePositionScore: (positionScore: PositionScore) => void
}

export function setCurrentEditedMasterId(ct: IContextState, masterId: string | undefined) {
    ct.currentEditedMasterId = masterId
}

function createContextState(
    items: BehaviorSubject<any[]>,
    currentAddItem: BehaviorSubject<any>,
    currentEditItem: BehaviorSubject<any>,
    result: BehaviorSubject<IResult | null>,
    resetAddItem: () => void,
    resetEditItem: (masterId: string | undefined) => void,
    getKey: () => string,
    setFieldAdd: (key: string, value: any) => void,
    setFieldEdit: (key: string, value: any) => void,
    sendResult: (result: IResult) => void
): IContextState {
    return {
        subjects: {
            items,
            currentAddItem,
            currentEditItem,
            result,
        },
        currentEditedMasterId: "",
        resetAddItem,
        resetEditItem,
        getKey,
        setFieldAdd,
        setFieldEdit,
        sendResult,
    }
}

export function createCategoryContextState(key: string): IContextState {
    const categoriesBS: BehaviorSubject<Category[]> = new BehaviorSubject<Category[]>([])
    const currentAddCategoryBS: BehaviorSubject<CreateCategory> = new BehaviorSubject<CreateCategory>(
        CreateCategoryFactory.create()
    )
    const currentEditCategoryBS: BehaviorSubject<ModifyCategory | null> = new BehaviorSubject<ModifyCategory | null>(
        null
    )
    const resultBS: BehaviorSubject<IResult | null> = new BehaviorSubject<IResult | null>(null)
    const resetAddCategory = () => {
        currentAddCategoryBS.next(CreateCategoryFactory.create())
    }
    const resetEditCategory = (masterId: string | undefined) => {
        if (masterId === undefined) {
            currentEditCategoryBS.next(null)
        } else {
            const cat = categoriesBS.value.find(c => c.masterId === masterId)
            if (cat === undefined) {
                currentEditCategoryBS.next(null)
                return
            }
            currentEditCategoryBS.next(ModifyCategoryFactory.from(cat))
        }
    }

    const getKey = () => {
        return key
    }

    const setFieldAdd = (key: string, value: any) => {
        const currentCopy = JSON.parse(JSON.stringify(currentAddCategoryBS.value))
        currentCopy[key] = value
        currentAddCategoryBS.next(currentCopy)
    }

    const setFieldEdit = (key: string, value: any) => {
        const currentCopy = JSON.parse(JSON.stringify(currentEditCategoryBS.value))
        if (!!currentCopy === false) {
            return
        }
        currentCopy[key] = value
        currentEditCategoryBS.next(currentCopy)
    }

    const sendResult = (result: IResult) => {
        resultBS.next(result)
        resultBS.next(null)
    }

    return createContextState(
        categoriesBS,
        currentAddCategoryBS,
        currentEditCategoryBS,
        resultBS,
        resetAddCategory,
        resetEditCategory,
        getKey,
        setFieldAdd,
        setFieldEdit,
        sendResult
    )
}

export function createLocationsContextState(key: string): IContextState {
    const locationsBS: BehaviorSubject<Location[]> = new BehaviorSubject<Location[]>([])
    const currentAddLocationBS: BehaviorSubject<CreateLocation> = new BehaviorSubject<CreateLocation>(
        CreateLocationFactory.create()
    )
    const currentEditLocationBS: BehaviorSubject<ModifyLocation | null> = new BehaviorSubject<ModifyLocation | null>(
        null
    )
    const resultBS: BehaviorSubject<IResult | null> = new BehaviorSubject<IResult | null>(null)
    const resetAddLocation = () => {
        currentAddLocationBS.next(CreateLocationFactory.create())
    }
    const resetEditLocation = (masterId: string | undefined) => {
        if (masterId === undefined) {
            currentEditLocationBS.next(null)
        } else {
            const loc = locationsBS.value.find(c => c.masterId === masterId)
            if (loc === undefined) {
                currentEditLocationBS.next(null)
                return
            }
            currentEditLocationBS.next(ModifyLocationFactory.from(loc))
        }
    }

    const getKey = () => {
        return key
    }

    const setFieldAdd = (key: string, value: any) => {
        const currentCopy = JSON.parse(JSON.stringify(currentAddLocationBS.value))
        currentCopy[key] = value
        currentAddLocationBS.next(currentCopy)
    }

    const setFieldEdit = (key: string, value: any) => {
        const currentCopy = JSON.parse(JSON.stringify(currentEditLocationBS.value))
        if (!!currentCopy === false) {
            return
        }
        currentCopy[key] = value
        currentEditLocationBS.next(currentCopy)
    }

    const sendResult = (result: IResult) => {
        resultBS.next(result)
        resultBS.next(null)
    }

    return createContextState(
        locationsBS,
        currentAddLocationBS,
        currentEditLocationBS,
        resultBS,
        resetAddLocation,
        resetEditLocation,
        getKey,
        setFieldAdd,
        setFieldEdit,
        sendResult
    )
}

export function createKngContextState(key: string): IContextState {
    const kngBS: BehaviorSubject<Application[]> = new BehaviorSubject<Application[]>([])
    const currentAddKngBS = new BehaviorSubject<null>(null)
    const currentEditKngBS: BehaviorSubject<ModifyApplication | null> = new BehaviorSubject<ModifyApplication | null>(
        null
    )
    const resultBS: BehaviorSubject<IResult | null> = new BehaviorSubject<IResult | null>(null)
    const resetAddKng = () => { }
    const resetEditKng = (masterId: string | undefined) => {
        if (masterId === undefined) {
            currentEditKngBS.next(null)
        } else {
            const kng = kngBS.value.find(c => c.masterId === masterId)
            if (kng === undefined) {
                currentEditKngBS.next(null)
                return
            }
            currentEditKngBS.next(ModifyApplicationFactory.from(kng))
        }
    }

    const getKey = () => {
        return key
    }

    const setFieldAdd = (key: string, value: any) => { }

    const setFieldEdit = (key: string, value: any) => {
        const currentCopy = JSON.parse(JSON.stringify(currentEditKngBS.value))
        if (!!currentCopy === false) {
            return
        }
        currentCopy[key] = value
        currentEditKngBS.next(currentCopy)
    }

    const sendResult = (result: IResult) => {
        resultBS.next(result)
        resultBS.next(null)
    }

    return createContextState(
        kngBS,
        currentAddKngBS,
        currentEditKngBS,
        resultBS,
        resetAddKng,
        resetEditKng,
        getKey,
        setFieldAdd,
        setFieldEdit,
        sendResult
    )
}

export function createKngSubmittedApplicationsState(): IKngSubmittedApplicationsState {
    const submittedApplicationsBS: BehaviorSubject<Application[]> = new BehaviorSubject<Application[]>([])
    const currentApplicationBS: BehaviorSubject<ModifyApplication | null> = new BehaviorSubject<ModifyApplication | null>(null)
    const currentSuperiorBS: BehaviorSubject<string> = new BehaviorSubject<string>("")
    const currentIndexBS: BehaviorSubject<number> = new BehaviorSubject<number>(-1)
    const resultBS: BehaviorSubject<IResult | null> = new BehaviorSubject<IResult | null>(null)
    const changedApplications: ModifyApplication[] = []
    const setFieldCurrentApplication = (key: string, value: any) => {
        const currentCopy = JSON.parse(JSON.stringify(currentApplicationBS.value))
        if (!!currentCopy === false) {
            return
        }
        currentCopy[key] = value
        currentApplicationBS.next(currentCopy)
    }
    const nextApplication = () => {
        if (submittedApplicationsBS.value.length === 0) {
            currentApplicationBS.next(null)
            return
        }
        changedApplications[currentIndexBS.value] = currentApplicationBS.value!
        if (currentApplicationBS.value === null) {
            currentIndexBS.next(0)
            currentApplicationBS.next(ModifyApplicationFactory.from(submittedApplicationsBS.value[0]))
            return
        }
        if (currentIndexBS.value + 1 >= submittedApplicationsBS.value.length) {
            currentIndexBS.next(0)
            currentApplicationBS.next(ModifyApplicationFactory.from(submittedApplicationsBS.value[0]))
            return
        }
        currentIndexBS.next(currentIndexBS.value + 1)
        currentApplicationBS.next(ModifyApplicationFactory.from(submittedApplicationsBS.value[currentIndexBS.value]))
    }
    const removeApplication = () => {
        changedApplications.splice(currentIndexBS.value, 1)
        submittedApplicationsBS.next(
            submittedApplicationsBS.value.filter(a => a.masterId !== currentApplicationBS.value!.masterId)
        )
        if (submittedApplicationsBS.value.length === 0) {
            currentApplicationBS.next(null)
            currentIndexBS.next(0)
            return
        }
        const newIdx = currentIndexBS.value >= submittedApplicationsBS.value.length
            ? 0
            : currentIndexBS.value
        currentIndexBS.next(newIdx)
        currentApplicationBS.next(ModifyApplicationFactory.from(submittedApplicationsBS.value[newIdx]))
    }
    const sendResult = (result: IResult) => {
        resultBS.next(result)
        resultBS.next(null)
    }
    const init = (apps: Application[]) => {
        submittedApplicationsBS.next(apps)
        if (apps.length > 0) {
            currentIndexBS.next(0)
            currentApplicationBS.next(ModifyApplicationFactory.from(apps[0]))
        } else {
            currentApplicationBS.next(null)
        }
        for (const app of apps) {
            changedApplications.push(ModifyApplicationFactory.from(app))
        }
    }

    const setCurrentApplication = (index: number) => {
        if (submittedApplicationsBS.value.length === 0) {
            currentApplicationBS.next(null)
            return
        }
        if (index === currentIndexBS.value && currentApplicationBS.value !== null) {
            return
        }
        changedApplications[currentIndexBS.value] = currentApplicationBS.value!
        if (index < 0 || index >= submittedApplicationsBS.value.length) {
            currentIndexBS.next(0)
            currentApplicationBS.next(ModifyApplicationFactory.from(submittedApplicationsBS.value[0]))
            return
        }
        currentIndexBS.next(index)
        currentApplicationBS.next(ModifyApplicationFactory.from(submittedApplicationsBS.value[index]))
    }

    const getChangedApplications = () => {
        changedApplications[currentIndexBS.value] = currentApplicationBS.value!
        return changedApplications
    }

    return {
        submittedApplications: submittedApplicationsBS,
        currentApplication: currentApplicationBS,
        currentSuperior: currentSuperiorBS,
        currentIndex: currentIndexBS,
        setFieldCurrentApplication,
        removeApplication,
        nextApplication,
        sendResult,
        setCurrentApplication,
        getChangedApplications,
        result: resultBS,
        init,
    }
}

export function createAverageMonthlyIncomeContextState(key: string): IContextState {
    const averageMonthlyIncomeBS: BehaviorSubject<AverageMonthlyIncome[]> = new BehaviorSubject<AverageMonthlyIncome[]>([])
    const currentAddAverageMonthlyIncomeBS: BehaviorSubject<CreateAverageMonthlyIncome> = new BehaviorSubject<CreateAverageMonthlyIncome>(
        CreateAverageMonthlyIncomeFactory.create()
    )
    const currentEditAverageMonthlyIncomeBS: BehaviorSubject<ModifyAverageMonthlyIncome | null> = new BehaviorSubject<ModifyAverageMonthlyIncome | null>(
        null
    )
    const resultBS: BehaviorSubject<IResult | null> = new BehaviorSubject<IResult | null>(null)
    const resetAddAverageMonthlyIncome = () => {
        currentAddAverageMonthlyIncomeBS.next(CreateAverageMonthlyIncomeFactory.create())
    }
    const resetEditAverageMonthlyIncome = (masterId: string | undefined) => {
        if (masterId === undefined) {
            currentEditAverageMonthlyIncomeBS.next(null)
        } else {
            const ami = averageMonthlyIncomeBS.value.find(ami => ami.masterId === masterId)
            if (ami === undefined) {
                currentEditAverageMonthlyIncomeBS.next(null)
                return
            }
            currentEditAverageMonthlyIncomeBS.next(ModifyAverageMonthlyIncomeFactory.from(ami))
        }
    }

    const getKey = () => {
        return key
    }

    const setFieldAdd = (key: string, value: any) => {
        const currentCopy = JSON.parse(JSON.stringify(currentAddAverageMonthlyIncomeBS.value))
        currentCopy[key] = value
        currentAddAverageMonthlyIncomeBS.next(currentCopy)
    }

    const setFieldEdit = (key: string, value: any) => {
        const currentCopy = JSON.parse(JSON.stringify(currentEditAverageMonthlyIncomeBS.value))
        if (!!currentCopy === false) {
            return
        }
        currentCopy[key] = value
        currentEditAverageMonthlyIncomeBS.next(currentCopy)
    }

    const sendResult = (result: IResult) => {
        resultBS.next(result)
        resultBS.next(null)
    }

    return createContextState(
        averageMonthlyIncomeBS,
        currentAddAverageMonthlyIncomeBS,
        currentEditAverageMonthlyIncomeBS,
        resultBS,
        resetAddAverageMonthlyIncome,
        resetEditAverageMonthlyIncome,
        getKey,
        setFieldAdd,
        setFieldEdit,
        sendResult
    )
}

export function createKngPositionScoresState(): IKngPositionScoresState {
    const positionScoresBS: BehaviorSubject<PositionScore[]> = new BehaviorSubject<PositionScore[]>([])
    const unassignedPositionsBS: BehaviorSubject<Position[]> = new BehaviorSubject<Position[]>([])
    const addPositionScore = (positionScore: PositionScore) => {
        const existing = positionScoresBS.value.find(ps => ps.positionId === positionScore.positionId)
        if (existing !== undefined) {
            return
        }
        positionScoresBS.next([...positionScoresBS.value, positionScore])
        unassignedPositionsBS.next(unassignedPositionsBS.value.filter(pos => pos.masterId !== positionScore.positionId))
    }
    const updatePositionScore = (positionScore: PositionScore) => {
        const existingIndex = positionScoresBS.value.findIndex(ps => ps.positionId === positionScore.positionId)
        if (existingIndex === -1) {
            return
        }
        const updatedScores = [...positionScoresBS.value]
        updatedScores[existingIndex] = positionScore
        positionScoresBS.next(updatedScores)
    }
    return {
        positionScores: positionScoresBS,
        unassignedPositions: unassignedPositionsBS,
        addPositionScore,
        updatePositionScore,
    }
}
